import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { api, errorMessage, post } from "@/lib/api";
import WorkThread, { messageSubmissionId, type WorkThreadData } from "@/components/courses/WorkThread";
import { ITEM_LABEL } from "@/pages/student/itemTypes";

type ParentThreadData = WorkThreadData & {
  student: { studentUserId: string; studentName: string };
  activity: { id: string; title: string; type: string };
  course: { id: string; title: string };
  replyBlock: "no_permission" | "not_started" | "chat_off" | null;
};

function closedHint(data: ParentThreadData | null) {
  if (!data || data.canReply) return undefined;
  switch (data.replyBlock) {
    case "not_started":
      return `You can write here once ${data.student.studentName} opens this activity.`;
    case "chat_off":
      return "The teacher has chat turned off here. You can still read it.";
    default:
      return "You can read this chat but not write in it.";
  }
}

export default function ParentActivityThread() {
  const { studentUserId = "", activityId = "" } = useParams();
  const [data, setData] = useState<ParentThreadData | null>(null);
  const [error, setError] = useState("");
  const [sendError, setSendError] = useState("");
  const [tab, setTab] = useState("all");
  const [sending, setSending] = useState(false);

  const base = `/api/parent/students/${encodeURIComponent(studentUserId)}/activities/${encodeURIComponent(activityId)}/thread`;

  useEffect(() => {
    let cancel = false;
    setData(null);
    setError("");
    api<{ success: boolean; data: ParentThreadData }>(base)
      .then((env) => {
        if (!cancel) setData(env.data);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [base]);

  const back = (
    <Link className="student-text-link" to="/parent/threads">
      <ChevronLeft size={15} /> Feedback
    </Link>
  );

  if (error) {
    return (
      <div className="student-page student-page--parent">
        {back}
        <div className="student-inline-error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="student-page student-page--parent" aria-busy="true" aria-live="polite">
        <span className="student-vh">Loading this conversation</span>
        <div className="student-ghost student-ghost--link" />
        <div className="student-ghost-head">
          <div className="student-ghost student-ghost--chip" />
          <div className="student-ghost student-ghost--title" />
          <div className="student-ghost student-ghost--line student-ghost--w60" />
        </div>
        <div className="student-ghost-card">
          <div className="student-ghost student-ghost--line" />
          <div className="student-ghost student-ghost--line student-ghost--w90" />
          <div className="student-ghost student-ghost--line student-ghost--w75" />
          <div className="student-ghost student-ghost--meta" />
        </div>
      </div>
    );
  }

  return (
    <div className="student-page student-page--parent">
      {back}
      <div className="student-page-head" style={{ marginTop: 10 }}>
        <span className="student-eyebrow">
          {data.student.studentName} · {data.course.title}
        </span>
        <h1>{data.activity.title}</h1>
        <p>
          {ITEM_LABEL[data.activity.type] ? `${ITEM_LABEL[data.activity.type]} · ` : ""}
          The chat between your student and their teacher.
        </p>
      </div>
      {sendError ? (
        <div className="student-inline-error" role="alert" style={{ marginBottom: 12 }}>
          {sendError}
        </div>
      ) : null}
      <section className="student-card parent-thread-card">
        <WorkThread
          data={data}
          tab={tab}
          audience="public"
          sending={sending}
          onTabChange={setTab}
          closedHint={closedHint(data)}
          onSend={async (body) => {
            setSending(true);
            setSendError("");
            try {
              const env = await post<{ success: boolean; data: ParentThreadData }>(`${base}/messages`, {
                submissionId: messageSubmissionId(tab, data.attempts),
                body,
              });
              setData(env.data);
            } catch (err) {
              setSendError(errorMessage(err));
            } finally {
              setSending(false);
            }
          }}
        />
      </section>
    </div>
  );
}
